/**
 * Bridge between the open Custom connectors editor and the gateway assistant.
 *
 * A collector's script lives in an unsaved modal form, so the assistant cannot
 * reach it through the API: the text it has to read and rewrite only exists in
 * the browser. While the editor is mounted it registers a handle here, and the
 * `get_collector_script` / `set_collector_script` tools go through it.
 *
 * Reading through a handle (rather than a snapshot captured when the drawer
 * opened) is what keeps an iteration honest — every turn sees the script as it
 * stands now, manual edits included.
 *
 * Module scope rather than `window`: both sides are in the same bundle, and the
 * section only ever opens one editor at a time. With no editor open every read
 * is null and every write is refused, so the tools fail closed.
 */

let editor = null;

/**
 * @param {Object} handle
 * @param {() => Object|null} handle.read   Live snapshot of the open collector.
 * @param {(patch: {script?: string, outputs?: Array}) => Object} handle.apply
 *   Applies a proposal to the form. Returns `{ok, data}` / `{ok, error}`.
 * @returns {() => void} unregister — call from the editor's effect cleanup.
 */
export function registerCollectorEditor(handle) {
  if (!handle || typeof handle.read !== "function" || typeof handle.apply !== "function") {
    throw new Error("registerCollectorEditor: a {read, apply} handle is required");
  }
  editor = handle;
  return () => {
    if (editor === handle) editor = null;
  };
}

export function collectorEditorIsOpen() {
  return editor !== null;
}

export function readCollectorEditor() {
  return editor ? editor.read() : null;
}

export function applyCollectorScript(patch) {
  if (!editor) return null;
  return editor.apply(patch || {});
}
